import {forwardRef, useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {atomButtonSizes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {criteoTrackEvents, useCriteoTrack} from '@adv-mt/mt-tracking-use-criteo'

import ActionAdFavoriteButton, {TYPES as actionAdFavoriteButtonTypes} from './actions-ad-favorite-button.js'

const TRACKING_ORIGINS = {
  LIST_AD_FAVORITED: 'List Ad Favorited',
  LIST_MENU_AD_FAVORITED_PRICEDROP: 'List 3p Menu Price Drop Ad Favorited',
  DETAIL_AD_FAVORITED: 'Detail Ad Favorited',
  DETAIL_PRICE_DROP_AD_FAVORITED: 'Detail Price Drop Ad Favorited',
  DETAIL_TOPBAR_AD_FAVORITED: 'Detail Topbar Ad Favorited',
  IMAGE_GALLERY_AD_FAVORITED: 'Image Gallery Ad Favorited',
  PROFESSIONAL_STOCK_AD_FAVORITED: 'Professional Stock Ad Favorited',
  ALERT_AD_FAVORITED: 'Alert Ad Favorited',
  COMPARATOR_AD_FAVORITED: 'Comparator',
  RELATED_AD_FAVORITED: 'Related Ad Favorited'
}
const UNFAVORITE_TRACKING_ORIGINS = {
  DETAIL_TOPBAR_AD_UNFAVORITED: 'Detail Topbar Ad Unfavorited',
  IMAGE_GALLERY_AD_UNFAVORITED: 'Image Gallery Ad Unfavorited'
}

const useAddFavorite = ({adId, setLoginModalVisible = () => {}, hitInformation2}) => {
  const [isLoginFlowCancelled, setIsLoginFlowCancelled] = useState(false)
  const [isAddedToFavorites, setIsAddedToFavorites] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)

  const {domain} = useContext(Context)
  const {triggerLoginModal, isLoginModalOpen} = useContext(LoginModalContext)

  const config = domain.get('config')
  const {LOGIN_SOURCE_ID_ADD_FAV} = config.get('login')
  const {HIT_ORIGINS} = config.get('tracking')

  useEffect(() => {
    showLoginModal &&
      triggerLoginModal({
        modalProps: {
          source: LOGIN_SOURCE_ID_ADD_FAV,
          trackingOrigin: HIT_ORIGINS.AD_FAVORITED,
          hitInformation2,
          onLogin: () => {
            domain
              .get('add_user_favorites_use_case')
              .execute({
                adId
              })
              .then(([, response]) => {
                response && setIsAddedToFavorites(true)
              })
          },
          onClose: () => {
            setIsLoginFlowCancelled(true)
            setShowLoginModal(false)
          }
        }
      })
  }, [showLoginModal, adId])

  useEffect(() => {
    setLoginModalVisible(isLoginModalOpen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoginModalOpen])

  return {isAddedToFavorites, isLoginFlowCancelled, setShowLoginModal, showLoginModal}
}

const ActionAdFavorite = forwardRef(
  (
    {
      ad = {},
      isFavorite = false,
      extendedTrackingData = {},
      isOnClickCallbackReady = true,
      onClick = () => {},
      size = atomButtonSizes.MEDIUM,
      trackingOrigin,
      unfavoriteTrackingOrigin,
      authenticationHitInformation2,
      type = actionAdFavoriteButtonTypes.ACTION_BUTTON,
      className,
      setLoginModalVisible = () => {}
    },
    ref
  ) => {
    const [isMarked, setIsMarked] = useState(isFavorite)

    const {domain} = useContext(Context)

    const {track} = useAnalyticsTrack()
    const {track: criteoEventTrack} = useCriteoTrack()

    const delayToNextFrame = useDelayToNextFrame()
    const {isAddedToFavorites, isLoginFlowCancelled, setShowLoginModal} = useAddFavorite({
      adId: ad.id,
      setLoginModalVisible,
      hitInformation2: authenticationHitInformation2
    })

    const config = domain.get('config')
    const {USER_NOT_LOGGED_IN_USER_ERROR} = config.get('ERRORS')
    const {TRACKING_EVENT_NAMES} = config.get('tracking')

    useEffect(() => {
      setIsMarked(isFavorite)
    }, [isFavorite])

    useEffect(() => {
      if (isAddedToFavorites) {
        delayedTracking(true)
      }
    }, [isAddedToFavorites, isLoginFlowCancelled])

    useEffect(() => {
      if (isAddedToFavorites) {
        setIsMarked(true)
        isOnClickCallbackReady && onClick(true)
      }
    }, [isAddedToFavorites, isOnClickCallbackReady])

    const delayedTracking = isAddingFavorite => {
      delayToNextFrame(() => {
        domain
          .get('get_event_tracking_data_use_case')
          .execute({
            ad,
            event: isAddingFavorite ? TRACKING_EVENT_NAMES.AD_FAVORITED : TRACKING_EVENT_NAMES.AD_UNFAVORITED,
            extendedTrackingData,
            origin: isAddingFavorite ? trackingOrigin : unfavoriteTrackingOrigin
          })
          .then(trackingData => track(trackingData))

        isAddingFavorite &&
          criteoEventTrack({
            event: criteoTrackEvents.ADD_TO_CART,
            eventData: {
              adId: ad.id
            }
          })
      })
    }

    const _handleToggleFavorite = async ev => {
      ev.stopPropagation()

      const handleFavUseCase = isMarked ? 'delete_user_favorites_use_case' : 'add_user_favorites_use_case'

      const [favUseCaseError] = await domain
        .get(handleFavUseCase)
        .execute({adId: ad.id, filteredProvinces: extendedTrackingData.provinces, trackingOrigin})

      if (favUseCaseError instanceof USER_NOT_LOGGED_IN_USER_ERROR) return setShowLoginModal(true)
      if (!favUseCaseError) {
        const isAddingFavorite = !isMarked
        setIsMarked(isAddingFavorite)
        delayedTracking(isAddingFavorite)
        isOnClickCallbackReady && onClick(isAddingFavorite)
      }
    }

    return (
      <ActionAdFavoriteButton
        ref={ref}
        isMarked={isMarked}
        onClick={_handleToggleFavorite}
        size={size}
        type={type}
        className={className}
      />
    )
  }
)

ActionAdFavorite.displayName = 'ActionAdFavorite'

ActionAdFavorite.propTypes = {
  ad: PropTypes.object,
  extendedTrackingData: PropTypes.object,
  isFavorite: PropTypes.bool,
  // Defers firing `onClick` until true — for consumers whose readiness to react depends on
  // async state that may resolve after the favorite itself was added (e.g. auth state post-login)
  isOnClickCallbackReady: PropTypes.bool,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(Object.values(atomButtonSizes)),
  trackingOrigin: PropTypes.oneOf(Object.values(TRACKING_ORIGINS)),
  unfavoriteTrackingOrigin: PropTypes.oneOf(Object.values(UNFAVORITE_TRACKING_ORIGINS)),
  authenticationHitInformation2: PropTypes.string,
  type: PropTypes.oneOf(Object.values(actionAdFavoriteButtonTypes)),
  className: PropTypes.string,
  // Callback to know when login modal is being shown
  setLoginModalVisible: PropTypes.func
}

export default ActionAdFavorite
export {actionAdFavoriteButtonTypes as actionAdFavoriteTypes}
export {atomButtonSizes as actionAdFavoriteButtonSizes}
export {
  TRACKING_ORIGINS as actionAdFavoriteTrackingOrigins,
  UNFAVORITE_TRACKING_ORIGINS as actionAdUnfavoriteTrackingOrigins
}
